import type { ReactNode } from "react";

import {
  hubPurchaseOrderStatusLabel,
  purchaseOrderFulfillmentStatusLabel,
  type MrpPurchaseOrder,
  type MrpPurchaseOrderLine,
  type MrpPurchaseOrderLineHoverRow,
} from "@/lib/mrp";
import { formatPurchaseOrderTimestamp } from "@/lib/mrpPurchaseOrderTimestamp";
import { EM_DASH } from "@/lib/utils";

import type { PurchaseOrderPeekContext } from "@/components/hub/entityPeekTypes";

const MAXIMUM_VISIBLE_PEEK_LINES = 4;

export type PurchaseOrderPeekRow = {
  label: string;
  value: ReactNode;
};

function peekField(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed != null && trimmed !== "" ? trimmed : EM_DASH;
}

function formatPeekPromiseDate(value: string | null | undefined): string {
  if (value == null || value.trim() === "") {
    return EM_DASH;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (match != null) {
    return `${match[2]}-${match[3]}-${match[1]}`;
  }
  return formatPurchaseOrderTimestamp(value);
}

function purchaseOrderStatusLabel(purchaseOrder: MrpPurchaseOrder): string {
  const hubStatus = purchaseOrder.hub_status;
  if (hubStatus != null) {
    return hubPurchaseOrderStatusLabel(hubStatus);
  }
  return peekField(purchaseOrder.mv_status);
}

function lineRowsForPeek(
  purchaseOrder: MrpPurchaseOrder,
  lines: readonly MrpPurchaseOrderLine[] | undefined,
): readonly MrpPurchaseOrderLineHoverRow[] {
  if (lines != null && lines.length > 0) {
    return lines.map((line) => ({
      sku: line.cpn,
      quantity_ordered: line.quantity_ordered,
      quantity_received: line.quantity_received,
    }));
  }
  return purchaseOrder.line_rows ?? [];
}

export function buildPurchaseOrderPeekRows(
  purchaseOrder: MrpPurchaseOrder,
  lines: readonly MrpPurchaseOrderLine[] | undefined,
  context?: PurchaseOrderPeekContext,
): PurchaseOrderPeekRow[] {
  const rows: PurchaseOrderPeekRow[] = [
    {
      label: "Purchase order",
      value: peekField(context?.purchase_order_number ?? purchaseOrder.po_number),
    },
    { label: "Supplier", value: peekField(purchaseOrder.supplier) },
    {
      label: "Location",
      value: peekField(purchaseOrder.location_code?.toUpperCase()),
    },
    { label: "Status", value: purchaseOrderStatusLabel(purchaseOrder) },
  ];

  const fulfillmentStatus = purchaseOrder.fulfillment_status;
  if (fulfillmentStatus != null) {
    rows.push({
      label: "Fulfillment",
      value: purchaseOrderFulfillmentStatusLabel(fulfillmentStatus),
    });
  }

  const promiseDate =
    purchaseOrder.next_expected_arrival?.trim() ||
    context?.promise_date?.trim() ||
    null;
  if (promiseDate != null) {
    rows.push({ label: "Promise", value: formatPeekPromiseDate(promiseDate) });
  }

  if (purchaseOrder.quantity_ordered > 0) {
    const received = purchaseOrder.quantity_received ?? 0;
    rows.push({
      label: "Progress",
      value: `${received.toLocaleString()}/${purchaseOrder.quantity_ordered.toLocaleString()}`,
    });
  }

  const lineRows = lineRowsForPeek(purchaseOrder, lines);
  const visibleLines = lineRows.slice(0, MAXIMUM_VISIBLE_PEEK_LINES);
  for (const line of visibleLines) {
    const received = line.quantity_received ?? 0;
    rows.push({
      label: peekField(line.sku),
      value: `${received.toLocaleString()}/${line.quantity_ordered.toLocaleString()}`,
    });
  }
  if (lineRows.length > MAXIMUM_VISIBLE_PEEK_LINES) {
    rows.push({
      label: "More lines",
      value: `+${String(lineRows.length - MAXIMUM_VISIBLE_PEEK_LINES)} more`,
    });
  }

  return rows;
}
